/*!
 * @jscrypto/modes v0.9.1
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
import { assertIv, assertBlockMultiple as assertBlockMultiple$2, concatBytes, equalBytes, assertBytes } from '@jscrypto/core';

const cbc = {
    kind: 'mode',
    name: 'CBC',
    requiresPadding: true,
    getIvSize: (cipher) => cipher.blockSize,
    createEncryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'CBC');
        return createCbcEncryptor(cipher, iv, getMutableInput$5(options));
    },
    createDecryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'CBC');
        return createCbcDecryptor(cipher, iv, getMutableInput$5(options));
    },
};
const cbcPreset = {
    kind: 'preset',
    name: 'cbc',
    components() {
        return [cbc];
    },
};
function createCbcEncryptor(cipher, iv, mutableInput) {
    let previous = iv;
    return {
        process(input) {
            assertBlockMultiple$1(cipher.blockSize, input);
            const output = mutableInput ? input : new Uint8Array(input.length);
            for (let offset = 0; offset < input.length; offset += cipher.blockSize) {
                for (let index = 0; index < cipher.blockSize; index++) {
                    output[offset + index] = input[offset + index] ^ previous[index];
                }
                if (cipher.encryptBlock) {
                    cipher.encryptBlock(output, offset, output, offset);
                }
                else {
                    const encrypted = output.subarray(offset, offset + cipher.blockSize);
                    cipher.encrypt(encrypted, encrypted);
                }
                previous = output.subarray(offset, offset + cipher.blockSize);
            }
            return output;
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function createCbcDecryptor(cipher, iv, mutableInput) {
    let previous = iv.slice();
    let current = new Uint8Array(cipher.blockSize);
    return {
        process(input) {
            assertBlockMultiple$1(cipher.blockSize, input);
            const output = mutableInput ? input : new Uint8Array(input.length);
            for (let offset = 0; offset < input.length; offset += cipher.blockSize) {
                current.set(input.subarray(offset, offset + cipher.blockSize));
                if (cipher.decryptBlock) {
                    cipher.decryptBlock(input, offset, output, offset);
                }
                else {
                    cipher.decrypt(input.subarray(offset, offset + cipher.blockSize), output.subarray(offset, offset + cipher.blockSize));
                }
                for (let index = 0; index < cipher.blockSize; index++) {
                    output[offset + index] ^= previous[index];
                }
                const nextPrevious = previous;
                previous = current;
                current = nextPrevious;
            }
            return output;
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function assertBlockMultiple$1(blockSize, input) {
    assertBlockMultiple$2(input, blockSize, 'CBC');
}
function getMutableInput$5(options) {
    if (typeof options !== 'object' || options === null) {
        return false;
    }
    return 'mutableInput' in options && options.mutableInput === true;
}

const cfb = {
    kind: 'mode',
    name: 'CFB',
    requiresPadding: false,
    getIvSize: (cipher) => cipher.blockSize,
    createEncryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'CFB');
        return createCfbEncryptor(cipher, iv, getMutableInput$4(options));
    },
    createDecryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'CFB');
        return createCfbDecryptor(cipher, iv, getMutableInput$4(options));
    },
};
const cfbPreset = {
    kind: 'preset',
    name: 'cfb',
    components() {
        return [cfb];
    },
};
function createCfbEncryptor(cipher, iv, mutableInput) {
    return createCfbTransform(cipher, iv, true, mutableInput);
}
function createCfbDecryptor(cipher, iv, mutableInput) {
    return createCfbTransform(cipher, iv, false, mutableInput);
}
function createCfbTransform(cipher, iv, encrypting, mutableInput) {
    let feedback = iv.slice();
    let nextFeedback = new Uint8Array(cipher.blockSize);
    let keystream = new Uint8Array(cipher.blockSize);
    let position = 0;
    return {
        process(input) {
            const output = mutableInput ? input : new Uint8Array(input.length);
            let offset = 0;
            if (position === 0) {
                for (; offset + cipher.blockSize <= input.length; offset += cipher.blockSize) {
                    encryptFeedback$1(cipher, feedback, keystream);
                    for (let index = 0; index < cipher.blockSize; index++) {
                        const inputByte = input[offset + index];
                        const outputByte = inputByte ^ keystream[index];
                        output[offset + index] = outputByte;
                        nextFeedback[index] = encrypting ? outputByte : inputByte;
                    }
                    const previousFeedback = feedback;
                    feedback = nextFeedback;
                    nextFeedback = previousFeedback;
                }
            }
            for (let i = offset; i < input.length; i++) {
                if (position === 0) {
                    encryptFeedback$1(cipher, feedback, keystream);
                }
                const inputByte = input[i];
                const outputByte = inputByte ^ keystream[position];
                output[i] = outputByte;
                nextFeedback[position] = encrypting ? outputByte : inputByte;
                position++;
                if (position === cipher.blockSize) {
                    const previousFeedback = feedback;
                    feedback = nextFeedback;
                    nextFeedback = previousFeedback;
                    position = 0;
                }
            }
            return output;
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function encryptFeedback$1(cipher, feedback, keystream) {
    if (cipher.encryptBlock) {
        cipher.encryptBlock(feedback, 0, keystream, 0);
    }
    else {
        cipher.encrypt(feedback, keystream);
    }
}
function getMutableInput$4(options) {
    if (typeof options !== 'object' || options === null) {
        return false;
    }
    return 'mutableInput' in options && options.mutableInput === true;
}

const ctr = {
    kind: 'mode',
    name: 'CTR',
    requiresPadding: false,
    getIvSize: (cipher) => cipher.blockSize,
    createEncryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'CTR');
        return createCtrTransform(cipher, iv, getMutableInput$3(options));
    },
    createDecryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'CTR');
        return createCtrTransform(cipher, iv, getMutableInput$3(options));
    },
};
const ctrPreset = {
    kind: 'preset',
    name: 'ctr',
    components() {
        return [ctr];
    },
};
function createCtrTransform(cipher, iv, mutableInput) {
    const counter = iv.slice();
    let keystream = new Uint8Array(cipher.blockSize);
    let position = cipher.blockSize;
    return {
        process(input) {
            const output = mutableInput ? input : new Uint8Array(input.length);
            let offset = 0;
            if (position === cipher.blockSize) {
                for (; offset + cipher.blockSize <= input.length; offset += cipher.blockSize) {
                    encryptCounter(cipher, counter, keystream);
                    incrementCounter$1(counter);
                    for (let index = 0; index < cipher.blockSize; index++) {
                        output[offset + index] = input[offset + index] ^ keystream[index];
                    }
                }
            }
            for (let i = offset; i < input.length; i++) {
                if (position === cipher.blockSize) {
                    encryptCounter(cipher, counter, keystream);
                    incrementCounter$1(counter);
                    position = 0;
                }
                output[i] = input[i] ^ keystream[position];
                position++;
            }
            return output;
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function encryptCounter(cipher, counter, keystream) {
    if (cipher.encryptBlock) {
        cipher.encryptBlock(counter, 0, keystream, 0);
    }
    else {
        cipher.encrypt(counter, keystream);
    }
}
function incrementCounter$1(counter) {
    for (let i = counter.length - 1; i >= 0; i--) {
        counter[i] = (counter[i] + 1) & 0xff;
        if (counter[i] !== 0) {
            return;
        }
    }
}
function getMutableInput$3(options) {
    if (typeof options !== 'object' || options === null) {
        return false;
    }
    return 'mutableInput' in options && options.mutableInput === true;
}

const ecb = {
    kind: 'mode',
    name: 'ECB',
    requiresPadding: true,
    getIvSize: () => 0,
    createEncryptor({ cipher, options }) {
        return createEcbEncryptor(cipher, getMutableInput$2(options));
    },
    createDecryptor({ cipher, options }) {
        return createEcbDecryptor(cipher, getMutableInput$2(options));
    },
};
const ecbPreset = {
    kind: 'preset',
    name: 'ecb',
    components() {
        return [ecb];
    },
};
function createEcbEncryptor(cipher, mutableInput) {
    return {
        process(input) {
            assertBlockMultiple(cipher.blockSize, input);
            const output = mutableInput ? input : new Uint8Array(input.length);
            return cipher.encrypt(input, output);
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function createEcbDecryptor(cipher, mutableInput) {
    return {
        process(input) {
            assertBlockMultiple(cipher.blockSize, input);
            const output = mutableInput ? input : new Uint8Array(input.length);
            return cipher.decrypt(input, output);
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function assertBlockMultiple(blockSize, input) {
    assertBlockMultiple$2(input, blockSize, 'ECB');
}
function getMutableInput$2(options) {
    if (typeof options !== 'object' || options === null) {
        return false;
    }
    return 'mutableInput' in options && options.mutableInput === true;
}

const gcm = {
    kind: 'mode',
    name: 'GCM',
    requiresPadding: false,
    getIvSize: () => 0,
    createEncryptor({ cipher, iv, options }) {
        return createGcmEncryptor(cipher, getNonce(iv, options), getAad(options), getTagLength(options), getMutableInput$1(options));
    },
    createDecryptor({ cipher, iv, options }) {
        return createGcmDecryptor(cipher, getNonce(iv, options), getAad(options), getTagLength(options), getTag(options), getMutableInput$1(options));
    },
};
const gcmPreset = {
    kind: 'preset',
    name: 'gcm',
    components() {
        return [gcm];
    },
};
const BLOCK_SIZE = 16;
const DEFAULT_TAG_LENGTH = 16;
const R_WORD = 0xe1000000;
const GHASH_WINDOW_BITS = 8;
function createGcmEncryptor(cipher, nonce, aad, tagLength, mutableInput) {
    assertGcmCipher(cipher);
    const auth = createGhash(encryptRawBlock(cipher, new Uint8Array(BLOCK_SIZE)));
    auth.updateFinal(aad);
    const j0 = createInitialCounter(auth.h, nonce);
    const tagMask = encryptRawBlock(cipher, j0);
    const counter = j0.slice();
    incrementCounter(counter);
    const xor = createCounterXor(cipher, counter);
    let ciphertextLength = 0;
    let pendingPlaintext = new Uint8Array(0);
    function encryptPlaintext(input, final = false, output) {
        if (input.length === 0) {
            return new Uint8Array(0);
        }
        const ciphertext = xor(input, output);
        if (final) {
            auth.updateFinal(ciphertext);
        }
        else {
            auth.update(ciphertext);
        }
        ciphertextLength += ciphertext.length;
        return ciphertext;
    }
    return {
        process(input) {
            if (pendingPlaintext.length) {
                input = concatBytes(pendingPlaintext, input);
            }
            const fullLength = input.length - (input.length % BLOCK_SIZE);
            pendingPlaintext = fullLength === input.length ? new Uint8Array(0) : input.slice(fullLength);
            if (!fullLength) {
                return new Uint8Array(0);
            }
            const fullInput = pendingPlaintext.length ? input.subarray(0, fullLength) : input;
            return encryptPlaintext(fullInput, false, mutableInput ? fullInput : undefined);
        },
        finalize(input = new Uint8Array(0)) {
            const ciphertext = input.length === 0 ? new Uint8Array(0) : this.process(input);
            const finalCiphertext = encryptPlaintext(pendingPlaintext, true, mutableInput ? pendingPlaintext : undefined);
            pendingPlaintext = new Uint8Array(0);
            const tag = createTag(auth, tagMask, aad.length, ciphertextLength, tagLength);
            return concatBytes(ciphertext, finalCiphertext, tag);
        },
    };
}
function createGcmDecryptor(cipher, nonce, aad, tagLength, detachedTag, mutableInput = false) {
    assertGcmCipher(cipher);
    const h = encryptRawBlock(cipher, new Uint8Array(BLOCK_SIZE));
    const j0 = createInitialCounter(h, nonce);
    const tagMask = encryptRawBlock(cipher, j0);
    let pendings = [];
    return {
        process(input) {
            pendings.push(input);
            return new Uint8Array(0);
        },
        finalize(input = new Uint8Array(0)) {
            if (input.length !== 0) {
                this.process(input);
            }
            const pending = pendings.length === 0
                ? new Uint8Array(0)
                : pendings.length === 1
                    ? pendings[0]
                    : concatBytes(...pendings);
            let ciphertext = pending;
            let tag = detachedTag;
            if (!tag) {
                if (pending.length < tagLength) {
                    throw new Error('GCM ciphertext must include an authentication tag.');
                }
                ciphertext = pending.subarray(0, pending.length - tagLength);
                tag = pending.subarray(pending.length - tagLength);
            }
            const auth = createGhash(h);
            auth.updateFinal(aad);
            auth.updateFinal(ciphertext);
            const expectedTag = createTag(auth, tagMask, aad.length, ciphertext.length, tagLength);
            if (!equalBytes(expectedTag, tag)) {
                throw new Error('GCM authentication failed.');
            }
            const counter = j0.slice();
            incrementCounter(counter);
            const plaintext = createCounterXor(cipher, counter)(ciphertext, mutableInput ? ciphertext : undefined);
            pendings = [];
            return plaintext;
        },
    };
}
function createGhash(h) {
    const table = createGhashTable(blockToWords(h), GHASH_WINDOW_BITS);
    const windowSize = 1 << GHASH_WINDOW_BITS;
    let state = createZeroWords();
    function updateWords(w0, w1, w2, w3) {
        state = multiplyGf128Window([
            (state[0] ^ w0) >>> 0,
            (state[1] ^ w1) >>> 0,
            (state[2] ^ w2) >>> 0,
            (state[3] ^ w3) >>> 0,
        ], table, windowSize);
    }
    function updateBlock(input, offset) {
        updateWords(readUint32BE(input, offset), readUint32BE(input, offset + 4), readUint32BE(input, offset + 8), readUint32BE(input, offset + 12));
    }
    function updatePartialBlock(input) {
        updateWords(readUint32BEPadded(input, 0), readUint32BEPadded(input, 4), readUint32BEPadded(input, 8), readUint32BEPadded(input, 12));
    }
    return {
        h,
        update(input) {
            for (let offset = 0; offset < input.length; offset += BLOCK_SIZE) {
                updateBlock(input, offset);
            }
        },
        updateFinal(input) {
            const fullLength = input.length - (input.length % BLOCK_SIZE);
            for (let offset = 0; offset < fullLength; offset += BLOCK_SIZE) {
                updateBlock(input, offset);
            }
            if (fullLength !== input.length) {
                updatePartialBlock(input.subarray(fullLength));
            }
        },
        digest(aadLength, ciphertextLength) {
            updateBlock(createLengthBlock(aadLength, ciphertextLength), 0);
            return wordsToBlock(state);
        },
    };
}
function createInitialCounter(h, nonce) {
    if (nonce.length === 0) {
        throw new Error('GCM mode requires a nonce.');
    }
    if (nonce.length === 12) {
        const j0 = new Uint8Array(BLOCK_SIZE);
        j0.set(nonce);
        j0[15] = 1;
        return j0;
    }
    const auth = createGhash(h);
    auth.updateFinal(nonce);
    return auth.digest(0, nonce.length);
}
function createCounterXor(cipher, counter) {
    return (input, target) => {
        const output = target ?? new Uint8Array(input.length);
        if (input.length !== 0) {
            const blocks = Math.ceil(input.length / BLOCK_SIZE);
            const keystream = new Uint8Array(blocks * BLOCK_SIZE);
            for (let offset = 0; offset < keystream.length; offset += BLOCK_SIZE) {
                keystream.set(counter, offset);
                incrementCounter(counter);
            }
            cipher.encrypt(keystream, keystream);
            for (let i = 0; i < input.length; i++) {
                output[i] = input[i] ^ keystream[i];
            }
        }
        return output;
    };
}
function encryptRawBlock(cipher, input) {
    const output = new Uint8Array(BLOCK_SIZE);
    return cipher.encrypt(input, output);
}
function createTag(auth, tagMask, aadLength, ciphertextLength, tagLength) {
    const tag = auth.digest(aadLength, ciphertextLength);
    for (let i = 0; i < tag.length; i++) {
        tag[i] ^= tagMask[i];
    }
    return tag.subarray(0, tagLength);
}
function createZeroWords() {
    return [0, 0, 0, 0];
}
// Adapted from @noble/ciphers' GHASH window-table approach (MIT).
function createGhashTable(h, windowBits) {
    const windowSize = 1 << windowBits;
    const windows = 128 / windowBits;
    const doubled = [];
    const value = [h[0], h[1], h[2], h[3]];
    for (let bit = 0; bit < 128; bit++) {
        doubled.push([value[0], value[1], value[2], value[3]]);
        shiftRightAndReduce(value);
    }
    const table = new Uint32Array(windows * windowSize * 4);
    for (let window = 0; window < windows; window++) {
        for (let selector = 0; selector < windowSize; selector++) {
            const item = createZeroWords();
            for (let bit = 0; bit < windowBits; bit++) {
                if (((selector >>> (windowBits - bit - 1)) & 1) !== 0) {
                    xorWordsInPlace(item, doubled[(window * windowBits) + bit]);
                }
            }
            const offset = ((window * windowSize) + selector) * 4;
            table[offset] = item[0];
            table[offset + 1] = item[1];
            table[offset + 2] = item[2];
            table[offset + 3] = item[3];
        }
    }
    return table;
}
function multiplyGf128Window(input, table, windowSize) {
    const output = createZeroWords();
    const mask = windowSize - 1;
    const windows = 128 / GHASH_WINDOW_BITS;
    for (let window = 0; window < windows; window++) {
        const bitOffset = window * GHASH_WINDOW_BITS;
        const word = bitOffset >>> 5;
        const shift = 32 - GHASH_WINDOW_BITS - (bitOffset & 31);
        const selector = (input[word] >>> shift) & mask;
        if (selector !== 0) {
            const tableOffset = ((window * windowSize) + selector) * 4;
            output[0] = (output[0] ^ table[tableOffset]) >>> 0;
            output[1] = (output[1] ^ table[tableOffset + 1]) >>> 0;
            output[2] = (output[2] ^ table[tableOffset + 2]) >>> 0;
            output[3] = (output[3] ^ table[tableOffset + 3]) >>> 0;
        }
    }
    return output;
}
function shiftRightAndReduce(words) {
    const lsb = words[3] & 1;
    words[3] = ((words[3] >>> 1) | ((words[2] & 1) << 31)) >>> 0;
    words[2] = ((words[2] >>> 1) | ((words[1] & 1) << 31)) >>> 0;
    words[1] = ((words[1] >>> 1) | ((words[0] & 1) << 31)) >>> 0;
    words[0] >>>= 1;
    if (lsb) {
        words[0] = (words[0] ^ R_WORD) >>> 0;
    }
}
function xorWordsInPlace(left, right) {
    left[0] = (left[0] ^ right[0]) >>> 0;
    left[1] = (left[1] ^ right[1]) >>> 0;
    left[2] = (left[2] ^ right[2]) >>> 0;
    left[3] = (left[3] ^ right[3]) >>> 0;
}
function incrementCounter(counter) {
    const value = ((((counter[12] << 24) >>> 0) |
        (counter[13] << 16) |
        (counter[14] << 8) |
        counter[15]) + 1) >>> 0;
    counter[12] = value >>> 24;
    counter[13] = value >>> 16;
    counter[14] = value >>> 8;
    counter[15] = value;
}
function createLengthBlock(aadLength, ciphertextLength) {
    const output = new Uint8Array(BLOCK_SIZE);
    writeBitLength64BE(aadLength, output, 0);
    writeBitLength64BE(ciphertextLength, output, 8);
    return output;
}
function writeBitLength64BE(byteLength, output, offset) {
    const high = Math.floor(byteLength / 0x20000000);
    const low = (byteLength % 0x20000000) * 8;
    writeUint32BE(high, output, offset);
    writeUint32BE(low, output, offset + 4);
}
function blockToWords(input) {
    return [
        readUint32BE(input, 0),
        readUint32BE(input, 4),
        readUint32BE(input, 8),
        readUint32BE(input, 12),
    ];
}
function wordsToBlock(words) {
    const output = new Uint8Array(BLOCK_SIZE);
    writeUint32BE(words[0], output, 0);
    writeUint32BE(words[1], output, 4);
    writeUint32BE(words[2], output, 8);
    writeUint32BE(words[3], output, 12);
    return output;
}
function readUint32BE(input, offset) {
    return (((input[offset] << 24) >>> 0) |
        (input[offset + 1] << 16) |
        (input[offset + 2] << 8) |
        input[offset + 3]) >>> 0;
}
function readUint32BEPadded(input, offset) {
    return ((((input[offset] ?? 0) << 24) >>> 0) |
        ((input[offset + 1] ?? 0) << 16) |
        ((input[offset + 2] ?? 0) << 8) |
        (input[offset + 3] ?? 0)) >>> 0;
}
function writeUint32BE(value, output, offset) {
    output[offset] = value >>> 24;
    output[offset + 1] = value >>> 16;
    output[offset + 2] = value >>> 8;
    output[offset + 3] = value;
}
function getNonce(iv, options) {
    const nonce = getOptions(options).nonce;
    if (nonce !== undefined) {
        assertBytes(nonce, 'GCM nonce');
        if (iv !== undefined) {
            throw new Error('GCM mode accepts either iv or nonce, not both.');
        }
        return nonce;
    }
    if (!iv) {
        throw new Error('GCM mode requires an IV/nonce.');
    }
    return iv;
}
function getAad(options) {
    const aad = getOptions(options).aad;
    if (aad === undefined) {
        return new Uint8Array(0);
    }
    assertBytes(aad, 'GCM aad');
    return aad;
}
function getTag(options) {
    const tag = getOptions(options).tag;
    if (tag === undefined) {
        return undefined;
    }
    assertBytes(tag, 'GCM tag');
    return tag;
}
function getTagLength(options) {
    const tag = getTag(options);
    if (tag) {
        return tag.length;
    }
    const tagLength = getOptions(options).tagLength;
    if (tagLength === undefined) {
        return DEFAULT_TAG_LENGTH;
    }
    if (typeof tagLength !== 'number' || !Number.isInteger(tagLength) || tagLength < 4 || tagLength > BLOCK_SIZE) {
        throw new RangeError('GCM tagLength must be an integer between 4 and 16 bytes.');
    }
    return tagLength;
}
function getOptions(options) {
    return typeof options === 'object' && options !== null ? options : {};
}
function getMutableInput$1(options) {
    if (typeof options !== 'object' || options === null) {
        return false;
    }
    return 'mutableInput' in options && options.mutableInput === true;
}
function assertGcmCipher(cipher) {
    if (cipher.blockSize !== BLOCK_SIZE) {
        throw new Error('GCM mode requires a 128-bit block cipher.');
    }
}

const ofb = {
    kind: 'mode',
    name: 'OFB',
    requiresPadding: false,
    getIvSize: (cipher) => cipher.blockSize,
    createEncryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'OFB');
        return createOfbTransform(cipher, iv, getMutableInput(options));
    },
    createDecryptor({ cipher, iv, options }) {
        assertIv(cipher.blockSize, iv, 'OFB');
        return createOfbTransform(cipher, iv, getMutableInput(options));
    },
};
const ofbPreset = {
    kind: 'preset',
    name: 'ofb',
    components() {
        return [ofb];
    },
};
function createOfbTransform(cipher, iv, mutableInput) {
    let feedback = iv.slice();
    let keystream = new Uint8Array(cipher.blockSize);
    let position = cipher.blockSize;
    return {
        process(input) {
            const output = mutableInput ? input : new Uint8Array(input.length);
            let offset = 0;
            if (position === cipher.blockSize) {
                for (; offset + cipher.blockSize <= input.length; offset += cipher.blockSize) {
                    encryptFeedback(cipher, feedback, keystream);
                    feedback.set(keystream);
                    for (let index = 0; index < cipher.blockSize; index++) {
                        output[offset + index] = input[offset + index] ^ keystream[index];
                    }
                }
            }
            for (let i = offset; i < input.length; i++) {
                if (position === cipher.blockSize) {
                    encryptFeedback(cipher, feedback, keystream);
                    feedback.set(keystream);
                    position = 0;
                }
                output[i] = input[i] ^ keystream[position];
                position++;
            }
            return output;
        },
        finalize(input = new Uint8Array(0)) {
            return input.length === 0 ? new Uint8Array(0) : this.process(input);
        },
    };
}
function encryptFeedback(cipher, feedback, keystream) {
    if (cipher.encryptBlock) {
        cipher.encryptBlock(feedback, 0, keystream, 0);
    }
    else {
        cipher.encrypt(feedback, keystream);
    }
}
function getMutableInput(options) {
    if (typeof options !== 'object' || options === null) {
        return false;
    }
    return 'mutableInput' in options && options.mutableInput === true;
}

function modesPreset() {
    return {
        kind: 'preset',
        name: 'modes',
        components() {
            return [cbcPreset, cfbPreset, ctrPreset, ecbPreset, gcmPreset, ofbPreset];
        },
    };
}

export { cbc, cbcPreset, cfb, cfbPreset, ctr, ctrPreset, ecb, ecbPreset, gcm, gcmPreset, modesPreset, ofb, ofbPreset };
//# sourceMappingURL=index.mjs.map
